import React from 'react'
import { Filter } from 'lucide-react'

import {
  Button,
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  useFilter,
} from '@rewardops/ui'
import { type CategorySummaryData } from '@rewardops/utils'

import { getLocalisedSortOptions } from '~/utils/filter-utils'
import BrandsListFilter from '~/components/maple/filters/brands-list-filter'
import CategoriesFilter from '~/components/maple/filters/categories-filter'
import FilterBase from '~/components/maple/filters/filter-base'
import PricePointFilter from '~/components/maple/filters/price-point-filter'
import { SortMenu } from '~/components/maple/filters/sort-menu'
import { TagsPicker } from '~/components/maple/filters/tags-picker'
import { useTranslation } from '~/app/i18n/client'
import { useApi } from '../hooks/use-api'
import CategoriesListFilter from './filters/categories-list-filter'
import EarnRatesFilter from './filters/earn-rates-filter'

export const MobileTopFilters = ({
  isPointsRangeEnabled = true,
  brandsOptions,
  categoriesOptions,
  earnRatesOptions,
  subCategories,
}: {
  isPointsRangeEnabled?: boolean
  brandsOptions?: any[] | undefined
  categoriesOptions?: any[] | undefined
  earnRatesOptions?: any[] | undefined
  subCategories?: CategorySummaryData[] | undefined
}) => {
  const { t } = useTranslation()
  const { tenant } = useApi()
  const [filterSheetOpen, setFilterSheetOpen] = React.useState(false)
  const [sortSheetOpen, setSortSheetOpen] = React.useState(false)
  const { sort } = useFilter()
  const defaultValue =
    getLocalisedSortOptions().find((o) => o.key === sort)?.message || undefined

  return (
    <div className="flex flex-col gap-4 px-2 pb-4">
      <div className="flex flex-row gap-4 py-2">
        <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
          <SheetTrigger>
            <Button>
              Filter <Filter className="ml-2" color="#8B96A5" size={18} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-full p-0">
            <div className="relative w-full border-b">
              <div className="px-[18px] py-4">
                <SheetTitle>Filters</SheetTitle>
              </div>
            </div>
            <div className="px-[18px]">
              <FilterBase
                title={t('filters.categories')}
                shouldBeHidden={!categoriesOptions?.length}
              >
                <CategoriesListFilter
                  categories={categoriesOptions ?? []}
                  searchEnabled={false}
                />
              </FilterBase>

              <FilterBase
                title={t('filters.category')}
                shouldBeHidden={!subCategories?.length}
                showTopSeparator={false}
              >
                <CategoriesFilter
                  categories={subCategories}
                  onSelectionSet={() => setFilterSheetOpen(false)}
                />
              </FilterBase>
              <FilterBase
                title={t('filters.brands')}
                shouldBeHidden={!brandsOptions || brandsOptions?.length == 0}
                showTopSeparator={false}
              >
                <BrandsListFilter
                  {...({
                    brands: ((brandsOptions as any) || []) as any,
                    searchEnabled: undefined as any,
                    onSelectionSet: () => setFilterSheetOpen(false),
                  } as any)}
                />
              </FilterBase>
              {isPointsRangeEnabled && (
                <FilterBase
                  title={t('filters.pointsRange')}
                  openByDefault
                  showTopSeparator={false}
                >
                  <PricePointFilter
                    onApply={() => {
                      setFilterSheetOpen(false)
                    }}
                  />
                </FilterBase>
              )}
              {tenant.flags?.earnRateFilterEnabled && (
                <FilterBase
                  title={t('filters.earnRate')}
                  openByDefault={true}
                  shouldBeHidden={
                    !earnRatesOptions || earnRatesOptions?.length === 0
                  }
                  showTopSeparator={false}
                >
                  <EarnRatesFilter
                    options={earnRatesOptions ?? []}
                    searchEnabled={false}
                  />
                </FilterBase>
              )}
            </div>
          </SheetContent>
        </Sheet>

        <Sheet open={sortSheetOpen} onOpenChange={setSortSheetOpen}>
          <SheetTrigger>
            <Button>{defaultValue ? defaultValue : 'Sort by'}</Button>
          </SheetTrigger>

          <SheetContent side="left" className="w-full p-0">
            <div className="relative w-full border-b">
              <div className="px-[18px] py-4">
                <SheetTitle>Sort</SheetTitle>
              </div>
            </div>
            <div className="px-[18px]">
              <SortMenu
                inputType="list"
                onSelectionSet={() => setSortSheetOpen(false)}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <TagsPicker
        brandOptions={brandsOptions}
        categoryOptions={categoriesOptions}
        earnRateOptions={earnRatesOptions}
      />
    </div>
  )
}
